import React from "react";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";

const SEO = ({
  title = "VidInterviewer - One-Way Video Interview Platform for Small Businesses",
  description = "Streamline your hiring process with our secure, AI-resistant video interview platform. Perfect for small businesses looking to conduct authentic candidate screenings through pre-recorded video interviews. Our virtual interview platform supports asynchronous video interviews, helping you evaluate candidates efficiently while avoiding AI-generated resumes. A modern alternative to HireVue interviews, our on-demand video interview solution makes hiring simple and effective.",
  image = "https://vidinterviewer.com/VidInterviewerLogo512.png",
  url = "https://vidinterviewer.com",
  type = "website",
  applicationName = "VidInterviewer",
  applicationDescription = "Professional video interview platform for businesses, specializing in one-way video interviews and pre-recorded interview solutions. Perfect for small businesses looking to streamline their hiring process with virtual interviews.",
  applicationOperatingSystem = "Web",
  applicationCategory = "BusinessApplication",
  applicationPrice = "0",
  applicationPriceCurrency = "USD",
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${url}#organization`,
        name: "VidInterviewer",
        url: url,
        logo: image,
        description:
          "An affordable virtual interview platform offering one-way video interviews and pre-recorded interview solutions for small businesses.",
        sameAs: [
          "https://www.linkedin.com/company/vidinterviewer",
          "https://twitter.com/vidinterviewer",
        ],
      },
      {
        "@type": "SoftwareApplication",
        name: applicationName,
        description: applicationDescription,
        operatingSystem: applicationOperatingSystem,
        applicationCategory: applicationCategory,
        keywords:
          "video interviewer, virtual interview, hiring for small businesses, ai resumes, one way video interview, hirevue interview, pre recorded video interview, interview platform, on demand video interview, asynchronous video interview, recorded video interview, pre recorded interview, virtual interview platform, recorded interview, cheap video interview, affordable video interview",
        offers: {
          "@type": "Offer",
          price: applicationPrice,
          priceCurrency: applicationPriceCurrency,
        },
      },
      {
        "@type": "WebApplication",
        name: applicationName,
        url: url,
        browserRequirements: "Requires JavaScript. Requires HTML5.",
        applicationCategory: applicationCategory,
        description:
          "A modern video interview platform for conducting one-way video interviews and pre-recorded interviews, perfect for small businesses.",
      },
    ],
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="keywords"
        content="video interviewer, virtual interview, hiring for small businesses, ai resumes, one way video interview, hirevue interview, pre recorded video interview, interview platform, on demand video interview, asynchronous video interview, recorded video interview, pre recorded interview, virtual interview platform, recorded interview, cheap video interview, affordable video interview"
      />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  applicationName: PropTypes.string,
  applicationDescription: PropTypes.string,
  applicationOperatingSystem: PropTypes.string,
  applicationCategory: PropTypes.string,
  applicationPrice: PropTypes.string,
  applicationPriceCurrency: PropTypes.string,
};

export default SEO;
